import { useState, useEffect } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Label } from '@/shared/components/ui/label'
import { Plus, Trash2, Upload, X, Loader2 } from 'lucide-react'
import type { ClothingItem, Category } from '@/shared/types'

interface ClothingItemFormProps {
  onCancel: () => void
  onSave: (data: ClothingItem, imageFiles?: File[]) => void
  item?: ClothingItem
  loading?: boolean
  categories: Category[]
}

type VariantWithFile = {
  id: string
  size: string
  color: string
  price: number
  stockQty: number
  imageUrl: string | File
  imageUrls?: string[]
  additionalImages?: (string | File)[]
  sku?: string
  imageFile?: File
  additionalImageFiles?: File[]
}

export function ClothingItemForm({ onCancel, onSave, item, loading = false, categories }: ClothingItemFormProps) {
  const sortedCategories = [...categories].sort((a, b) => a.name.localeCompare(b.name))
  const [name, setName] = useState(item?.name || '')
  const [description, setDescription] = useState(item?.description || '')
  const [brand, setBrand] = useState(item?.brand || '')
  const [selectedCategories, setSelectedCategories] = useState<string[]>(item?.categories?.map((c) => c.id) || [])
  const [errors, setErrors] = useState<Record<string, string>>({})

  const createEmptyVariant = (): VariantWithFile => ({
    id: crypto.randomUUID(),
    size: '',
    color: '',
    price: 0,
    stockQty: 0,
    imageUrl: '',
    imageUrls: [],
    additionalImages: [],
    sku: '',
    additionalImageFiles: [],
  })

  const [variants, setVariants] = useState<VariantWithFile[]>(
    item?.variants.length
      ? item.variants.map((v) => ({
          id: v.id || crypto.randomUUID(),
          size: v.size || '',
          color: v.color || '',
          price: v.price || 0,
          stockQty: v.stockQty || 0,
          imageUrl: v.imageUrl || '',
          imageUrls: v.imageUrls || [],
          additionalImages: v.imageUrls || [],
          sku: v.sku || '',
          imageFile: undefined,
          additionalImageFiles: [],
        }))
      : [createEmptyVariant()]
  )

  useEffect(() => {
    if (item) {
      setName(item.name || '')
      setDescription(item.description || '')
      setBrand(item.brand || '')
      setVariants(
        item.variants.map((v) => ({
          id: v.id || crypto.randomUUID(),
          size: v.size || '',
          color: v.color || '',
          price: v.price || 0,
          stockQty: v.stockQty || 0,
          imageUrl: v.imageUrl || '',
          imageUrls: v.imageUrls || [],
          additionalImages: v.imageUrls || [],
          sku: v.sku || '',
          imageFile: undefined,
          additionalImageFiles: [],
        }))
      )
      setSelectedCategories(item.categories?.map((c) => c.id) || [])
    }
  }, [item])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (name.trim().length < 4) newErrors.name = 'Name must be at least 4 characters'
    if (description.trim().length < 20) newErrors.description = 'Description must be at least 20 characters'
    if (!brand.trim()) newErrors.brand = 'Brand is required'
    if (variants.length === 0) newErrors.variants = 'At least one variant is required'

    variants.forEach((variant, index) => {
      if (!variant.size.trim()) newErrors[`variant-${index}-size`] = 'Size is required'
      if (!variant.color.trim()) newErrors[`variant-${index}-color`] = 'Color is required'
      if (variant.price <= 0) newErrors[`variant-${index}-price`] = 'Price must be greater than 0'
      if (variant.stockQty < 0) newErrors[`variant-${index}-stock`] = 'Stock cannot be negative'
      if (!item && !variant.imageFile && !variant.imageUrl) {
        newErrors[`variant-${index}-image`] = 'Image is required'
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const addVariant = () => setVariants([...variants, createEmptyVariant()])

  const removeVariant = (index: number) => {
    if (variants.length > 1) setVariants(variants.filter((_, i) => i !== index))
  }

  const updateVariant = (index: number, field: string, value: string | number | File | undefined) => {
    setVariants((prev) =>
      prev.map((v, i) => (i !== index ? v : { ...v, [field]: value }))
    )
    if (errors[`variant-${index}-${field}`]) {
      setErrors({ ...errors, [`variant-${index}-${field}`]: '' })
    }
  }

  const handlePrimaryImageChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      setErrors({ ...errors, [`variant-${index}-image`]: 'Image size must be less than 5MB' })
      return
    }

    setVariants((prev) =>
      prev.map((v, i) =>
        i === index ? { ...v, imageUrl: URL.createObjectURL(file), imageFile: file } : v
      )
    )
    setErrors({ ...errors, [`variant-${index}-image`]: '' })
  }

  const handleAdditionalImagesChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    const variant = variants[index]
    if (!variant) return
    
    const currentCount = variant.additionalImages?.length || 0
    const maxAllowed = 5 - currentCount

    let validFiles = files.filter((f) => f.size <= 5 * 1024 * 1024)

    if (validFiles.length > maxAllowed) {
      validFiles = validFiles.slice(0, maxAllowed)
      setErrors({
        ...errors,
        [`variant-${index}-additional`]: `Only ${maxAllowed} more image(s) allowed. Max 6 images per variant.`,
      })
    } else if (validFiles.length < files.length) {
      setErrors({
        ...errors,
        [`variant-${index}-additional`]: 'Some images exceeded 5MB and were skipped',
      })
    }

    if (validFiles.length > 0) {
      setVariants((prev) =>
        prev.map((v, i) =>
          i === index
            ? {
                ...v,
                additionalImages: [...(v.additionalImages || []), ...validFiles.map((f) => URL.createObjectURL(f))],
                additionalImageFiles: [...(v.additionalImageFiles || []), ...validFiles],
              }
            : v
        )
      )
    }
    e.target.value = ''
  }

  const removeAdditionalImage = (variantIndex: number, imgIdx: number, img: string | File) => {
    const isNewUpload = typeof img === 'string' && img.startsWith('blob:')

    setVariants((prev) =>
      prev.map((v, i) => {
        if (i !== variantIndex) return v

        const newAdditionalImages = v.additionalImages?.filter((_, j) => j !== imgIdx)
        let newAdditionalImageFiles = v.additionalImageFiles

        if (isNewUpload && v.additionalImages && v.additionalImageFiles) {
          const blobIndexBeforeThis = v.additionalImages
            .slice(0, imgIdx)
            .filter((url) => typeof url === 'string' && url.startsWith('blob:')).length
          newAdditionalImageFiles = v.additionalImageFiles.filter((_, j) => j !== blobIndexBeforeThis)
        }

        return { ...v, additionalImages: newAdditionalImages, additionalImageFiles: newAdditionalImageFiles }
      })
    )
  }

  const handleSave = () => {
    if (!validate()) return

    const imageFiles: File[] = []
    variants.forEach((v) => {
      if (v.imageFile) imageFiles.push(v.imageFile)
      if (v.additionalImageFiles?.length) imageFiles.push(...v.additionalImageFiles)
    })

    if (imageFiles.length === 0 && !item) {
      setErrors({ ...errors, variants: 'At least one variant must have an image' })
      return
    }

    const data: ClothingItem = {
      id: item?.id || crypto.randomUUID(),
      name: name.trim(),
      description: description.trim(),
      brand: brand.trim(),
      imageUrl: variants[0]?.imageUrl && typeof variants[0].imageUrl === 'string' ? variants[0].imageUrl : '',
      categories: categories.filter((c) => selectedCategories.includes(c.id)),
      variants: variants.map((v) => ({
        id: v.id || crypto.randomUUID(),
        size: v.size.trim(),
        color: v.color.trim(),
        price: v.price || 0,
        stockQty: v.stockQty || 0,
        imageUrl: v.imageUrl || '',
        imageUrls: v.additionalImages?.filter((img): img is string => typeof img === 'string') || [],
        sku: v.sku || '',
      })),
    }

    onSave(data, imageFiles.length > 0 ? imageFiles : undefined)
  }

  const getVariantPreview = (imageUrl: string | File | undefined, imageFile?: File) => {
    if (imageFile) return URL.createObjectURL(imageFile)
    if (!imageUrl) return null
    return typeof imageUrl === 'string' ? imageUrl : URL.createObjectURL(imageUrl)
  }

  return (
    <div className="space-y-6 py-4">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <Label htmlFor="name">Product Name <span className="text-destructive">*</span></Label>
          <Input
            id="name"
            placeholder="e.g., Classic White Tee"
            value={name}
            onChange={(e) => { setName(e.target.value); setErrors({ ...errors, name: '' }) }}
            className={errors.name ? 'border-destructive' : ''}
          />
          {errors.name && <p className="text-sm text-destructive mt-1">{errors.name}</p>}
        </div>

        <div className="col-span-2">
          <Label htmlFor="description">Description <span className="text-destructive">*</span></Label>
          <Textarea
            id="description"
            placeholder="Describe the product in detail (minimum 20 characters)..."
            rows={3}
            value={description}
            onChange={(e) => { setDescription(e.target.value); setErrors({ ...errors, description: '' }) }}
            className={errors.description ? 'border-destructive' : ''}
          />
          {errors.description && <p className="text-sm text-destructive mt-1">{errors.description}</p>}
        </div>

        <div>
          <Label htmlFor="brand">Brand <span className="text-destructive">*</span></Label>
          <Input
            id="brand"
            placeholder="e.g., Urban Basics"
            value={brand}
            onChange={(e) => { setBrand(e.target.value); setErrors({ ...errors, brand: '' }) }}
            className={errors.brand ? 'border-destructive' : ''}
          />
          {errors.brand && <p className="text-sm text-destructive mt-1">{errors.brand}</p>}
        </div>

        <div className="col-span-2 space-y-2">
          <Label>Categories</Label>
          {sortedCategories.length === 0 ? (
            <p className="text-sm text-muted-foreground">No categories yet. Create categories from your dashboard.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {sortedCategories.map((category) => (
                <Button
                  key={category.id}
                  type="button"
                  size="sm"
                  variant={selectedCategories.includes(category.id) ? 'default' : 'outline'}
                  onClick={() =>
                    setSelectedCategories((prev) =>
                      prev.includes(category.id) ? prev.filter((id) => id !== category.id) : [...prev, category.id]
                    )
                  }
                  disabled={loading}
                  className="rounded-full"
                >
                  {category.name}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Variants Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-lg font-medium">Variants <span className="text-destructive">*</span></Label>
          <Button type="button" variant="outline" size="sm" onClick={addVariant} disabled={loading}>
            <Plus className="w-4 h-4 mr-2" /> Add Variant
          </Button>
        </div>

        {errors.variants && <p className="text-sm text-destructive">{errors.variants}</p>}

        <div className="space-y-4">
          {variants.map((variant, index) => (
            <VariantCard
              key={variant.id || index}
              variant={variant}
              index={index}
              isEdit={!!item}
              loading={loading}
              errors={errors}
              preview={getVariantPreview(variant.imageUrl, variant.imageFile)}
              onUpdate={updateVariant}
              onRemove={removeVariant}
              onPrimaryImageChange={handlePrimaryImageChange}
              onAdditionalImagesChange={handleAdditionalImagesChange}
              onRemoveAdditionalImage={removeAdditionalImage}
              onClearPrimaryImage={() => {
                setVariants((prev) => prev.map((v, i) => i === index ? { ...v, imageUrl: '', imageFile: undefined } : v))
                setErrors({ ...errors, [`variant-${index}-image`]: '' })
              }}
              canRemove={variants.length > 1}
            />
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-6 border-t">
        <Button variant="outline" onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button onClick={handleSave} disabled={loading}>
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{item ? 'Updating...' : 'Adding...'}</>
          ) : (
            `${item ? 'Update' : 'Add'} Product`
          )}
        </Button>
      </div>
    </div>
  )
}

// ======================== VARIANT CARD ========================
interface VariantCardProps {
  variant: VariantWithFile
  index: number
  isEdit: boolean
  loading: boolean
  errors: Record<string, string>
  preview: string | null
  canRemove: boolean
  onUpdate: (index: number, field: string, value: string | number | File | undefined) => void
  onRemove: (index: number) => void
  onPrimaryImageChange: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void
  onAdditionalImagesChange: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveAdditionalImage: (variantIndex: number, imgIdx: number, img: string | File) => void
  onClearPrimaryImage: () => void
}

function VariantCard({
  variant,
  index,
  isEdit,
  loading,
  errors,
  preview,
  canRemove,
  onUpdate,
  onRemove,
  onPrimaryImageChange,
  onAdditionalImagesChange,
  onRemoveAdditionalImage,
  onClearPrimaryImage,
}: VariantCardProps) {
  return (
    <div className="p-5 border rounded-lg bg-muted/30">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
        {/* Primary Image */}
        <div className="md:col-span-2">
          <Label className="text-xs">Primary Image {!isEdit && <span className="text-destructive">*</span>}</Label>
          <div className="mt-2">
            {preview ? (
              <div className="relative inline-block">
                <img src={preview} alt={`Variant ${index + 1}`} className="h-32 w-32 object-cover rounded-lg border" />
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  className="absolute top-1 right-1 h-6 w-6"
                  onClick={onClearPrimaryImage}
                  disabled={loading}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors">
                <Upload className="w-8 h-8 text-muted-foreground" />
                <span className="text-xs mt-1">Upload</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onPrimaryImageChange(index, e)} disabled={loading} />
              </label>
            )}
          </div>
          {errors[`variant-${index}-image`] && <p className="text-xs text-destructive mt-1">{errors[`variant-${index}-image`]}</p>}
        </div>

        {/* Size */}
        <div>
          <Label className="text-xs">Size <span className="text-destructive">*</span></Label>
          <Input
            placeholder="M"
            value={variant.size || ''}
            onChange={(e) => onUpdate(index, 'size', e.target.value)}
            className={errors[`variant-${index}-size`] ? 'border-destructive' : ''}
          />
          {errors[`variant-${index}-size`] && <p className="text-xs text-destructive mt-1">{errors[`variant-${index}-size`]}</p>}
        </div>

        {/* Color */}
        <div>
          <Label className="text-xs">Color <span className="text-destructive">*</span></Label>
          <Input
            placeholder="White"
            value={variant.color || ''}
            onChange={(e) => onUpdate(index, 'color', e.target.value)}
            className={errors[`variant-${index}-color`] ? 'border-destructive' : ''}
          />
          {errors[`variant-${index}-color`] && <p className="text-xs text-destructive mt-1">{errors[`variant-${index}-color`]}</p>}
        </div>

        {/* Price */}
        <div>
          <Label className="text-xs">Price (₹) <span className="text-destructive">*</span></Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="599"
            value={variant.price || ''}
            onChange={(e) => onUpdate(index, 'price', Number(e.target.value))}
            className={errors[`variant-${index}-price`] ? 'border-destructive' : ''}
          />
          {errors[`variant-${index}-price`] && <p className="text-xs text-destructive mt-1">{errors[`variant-${index}-price`]}</p>}
        </div>

        {/* Stock + Remove */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs">Stock <span className="text-destructive">*</span></Label>
            <Input
              type="number"
              min="0"
              placeholder="10"
              value={variant.stockQty || ''}
              onChange={(e) => onUpdate(index, 'stockQty', Number(e.target.value))}
              className={errors[`variant-${index}-stock`] ? 'border-destructive' : ''}
            />
            {errors[`variant-${index}-stock`] && <p className="text-xs text-destructive mt-1">{errors[`variant-${index}-stock`]}</p>}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={!canRemove || loading}
            onClick={() => onRemove(index)}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Additional Images */}
      <div className="mt-4 pt-4 border-t border-dashed">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs text-muted-foreground">Additional Images (Gallery)</Label>
          <span className="text-xs text-muted-foreground">{variant.additionalImages?.length || 0}/5 images</span>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          {variant.additionalImages?.map((img, imgIdx) => {
            const imgSrc = typeof img === 'string' ? img : URL.createObjectURL(img)
            return (
              <div key={imgIdx} className="relative group">
                <img src={imgSrc} alt={`Additional ${imgIdx + 1}`} className="h-20 w-20 object-cover rounded-lg border" />
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  className="absolute -top-2 -right-2 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => onRemoveAdditionalImage(index, imgIdx, img)}
                  disabled={loading}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )
          })}

          {(variant.additionalImages?.length || 0) < 5 && (
            <label className="flex flex-col items-center justify-center w-20 h-20 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 transition-colors bg-muted/20">
              <Plus className="w-5 h-5 text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground mt-0.5">Add</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onAdditionalImagesChange(index, e)} disabled={loading} />
            </label>
          )}
        </div>
        {errors[`variant-${index}-additional`] && <p className="text-xs text-destructive mt-1">{errors[`variant-${index}-additional`]}</p>}
      </div>
    </div>
  )
}
